import React, { useState, type FormEvent } from "react";
import {
  astryx,
  AuthCard,
  AuthLayout,
  AuthLink,
  AuthSubmitButton,
  PasswordInput,
} from "@shadowcue/ui";
import useNativeFormSubmit from "../../hooks/useNativeFormSubmit";

interface ResetPasswordProps {
  formAction: string;
  authenticityToken: string;
  resetPasswordToken: string;
  minimumPasswordLength: number | null;
  /** Devise's validation messages, each shown on the field it belongs to. */
  passwordError: string | null;
  passwordConfirmationError: string | null;
  /** An invalid or expired link. No field can fix it, so it gets a Banner. */
  resetLinkError: { title: string; detail: string } | null;
  forgotPasswordPath: string;
  signInPath: string;
}

/**
 * Sets a new password from the link in the reset email. Posts natively to
 * Devise, which signs the user in on success, or re-renders this page with
 * the errors.
 *
 * Errors render inside the card, on the field they belong to, so a failed
 * attempt never changes the page layout.
 */
const ResetPassword = (props: ResetPasswordProps) => {
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");
  const { isSubmitting, markSubmitting } = useNativeFormSubmit();

  // Devise never refills password fields, so a field that's still empty is
  // one the user hasn't retyped since the error. Typing clears its error.
  const passwordError = password === "" ? props.passwordError : null;
  const passwordConfirmationError =
    passwordConfirmation === "" ? props.passwordConfirmationError : null;

  const passwordPlaceholder = props.minimumPasswordLength
    ? `New password (${props.minimumPasswordLength}+ characters)`
    : "New password";

  const canSubmit = password !== "" && passwordConfirmation !== "" && !isSubmitting;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!canSubmit) {
      event.preventDefault();
      return;
    }
    markSubmitting();
  };

  return (
    <AuthLayout>
      <form method="post" action={props.formAction} onSubmit={onSubmit}>
        <input type="hidden" name="_method" value="put" />
        <input type="hidden" name="authenticity_token" value={props.authenticityToken} />
        <input type="hidden" name="user[reset_password_token]" value={props.resetPasswordToken} />
        <AuthCard subtitle="Choose a new password for your account.">
          {props.resetLinkError && (
            <astryx.Banner
              status="error"
              title={props.resetLinkError.title}
              description={
                <>
                  {props.resetLinkError.detail}{" "}
                  <AuthLink
                    href={props.forgotPasswordPath}
                    type="inherit"
                    hasUnderline
                  >
                    Request a new link
                  </AuthLink>
                </>
              }
              container="card"
            />
          )}

          <astryx.Stack direction="vertical" gap={3}>
            <PasswordInput
              label="New password"
              value={password}
              onChange={setPassword}
              placeholder={passwordPlaceholder}
              htmlName="user[password]"
              errorMessage={passwordError}
              hasAutoFocus
            />
            <PasswordInput
              label="Confirm new password"
              value={passwordConfirmation}
              onChange={setPasswordConfirmation}
              placeholder="Confirm new password"
              htmlName="user[password_confirmation]"
              errorMessage={passwordConfirmationError}
            />
          </astryx.Stack>

          <astryx.Stack direction="vertical" gap={3}>
            <AuthSubmitButton
              type="submit"
              label={isSubmitting ? "Resetting…" : "Reset password"}
              variant="primary"
              width="100%"
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            />
            <astryx.Stack direction="horizontal" justify="center">
              <AuthLink href={props.signInPath}>
                Back to sign in
              </AuthLink>
            </astryx.Stack>
          </astryx.Stack>
        </AuthCard>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
